import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext.js"; // Import useAuth to get the token

const API_BASE_URL = "/api";

export const useDiverCertifications = () => {
  const { userObjectId, currentUser } = useAuth(); // Get currentUser to access getIdToken

  const [diverCerts, setDiverCerts] = useState([]);
  const [agencyCerts, setAgencyCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to get authorization headers
  const getAuthHeaders = useCallback(async () => {
    if (!currentUser) {
      return {}; // No current user, no token
    }
    try {
      const token = await currentUser.getIdToken();
      return {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
    } catch (tokenError) {
      console.error("Error getting auth token:", tokenError);
      setError("Authentication error. Please try logging in again.");
      return {}; // Return empty headers on error
    }
  }, [currentUser]); // Recalculate if currentUser changes

  // Function to fetch diver's certifications
  const fetchDiverCertifications = useCallback(async () => {
    if (!userObjectId) {
      if (currentUser === null) {
        setError(
          "You are not logged in. Please log in to view your certifications."
        );
      } else if (currentUser) {
        setError("Your user data is still loading. Please wait.");
      }
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const config = await getAuthHeaders(); // Get the headers
      const response = await axios.get(
        `${API_BASE_URL}/certifications/diver/${userObjectId}`,
        config
      );
      setDiverCerts(response.data);
    } catch (err) {
      console.error("Error fetching diver certifications:", err);
      // Handle 403 Forbidden specifically if needed, or rely on middleware
      if (err.response && err.response.status === 403) {
        setError(
          "Access forbidden. You don't have permission to view these certifications."
        );
      } else {
        setError(
          err.response?.data?.error || "Failed to load your certifications."
        );
      }
    } finally {
      setLoading(false);
    }
  }, [userObjectId, currentUser, getAuthHeaders]); // Add getAuthHeaders to dependencies

  // Function to fetch agency certifications (inventory)
  const fetchAgencyCertifications = useCallback(async () => {
    setError(null);
    try {
      // Agency certifications DOES require authentication because it's behind VerifyToken
      const config = await getAuthHeaders(); // <-- UNCOMMENT THIS LINE
      const response = await axios.get(
        `${API_BASE_URL}/certifications/agency`,
        config // <-- UNCOMMENT THIS LINE
      );
      setAgencyCerts(response.data);
    } catch (err) {
      console.error("Error fetching agency certifications:", err);
      // Add specific error handling for 401 if you wish
      if (err.response && err.response.status === 401) {
        setError(
          "Authentication required to load available certification types."
        );
      } else {
        setError(
          err.response?.data?.error ||
            "Failed to load available certification types."
        );
      }
    }
  }, [getAuthHeaders]); // This dependency is already correct because you're using getAuthHeaders
  // Function to add a new diver certification
  const addDiverCertification = useCallback(
    async (newCertData) => {
      if (!userObjectId) {
        setError("Diver ID is missing. Cannot add certification.");
        return null;
      }

      setLoading(true);
      setError(null);
      try {
        const config = await getAuthHeaders(); // Get the headers
        const payload = {
          ...newCertData,
          certificationDate: newCertData.certificationDate
            ? new Date(newCertData.certificationDate).toISOString()
            : null,
          expirationDate: newCertData.expirationDate
            ? new Date(newCertData.expirationDate).toISOString()
            : null,
        };

        const response = await axios.post(
          `${API_BASE_URL}/certifications/diver/${userObjectId}`,
          payload,
          config
        );
        setDiverCerts((prev) => [...prev, response.data]);
        return response.data;
      } catch (err) {
        console.error("Error adding diver certification:", err);
        if (err.response && err.response.status === 403) {
          setError(
            "Access forbidden. You don't have permission to add this certification."
          );
        } else {
          setError(
            err.response?.data?.error ||
              "Failed to add certification. Please check your inputs."
          );
        }
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [userObjectId, currentUser, getAuthHeaders]
  ); // Add getAuthHeaders to dependencies

  // Initial fetches on component mount
  useEffect(() => {
    fetchAgencyCertifications();
  }, [fetchAgencyCertifications]);

  // Fetch diver certs whenever userObjectId or currentUser changes
  useEffect(() => {
    if (userObjectId) {
      // Only fetch if userObjectId is truly available
      fetchDiverCertifications();
    }
  }, [fetchDiverCertifications, userObjectId]); // Add userObjectId to trigger re-fetch when it becomes available

  return {
    diverCerts,
    agencyCerts,
    loading,
    error,
    fetchDiverCertifications,
    addDiverCertification,
    currentUser,
    userObjectId,
  };
};
